import React from "react";
import BeforeAfterGallery from "@/components/site/BeforeAfterGallery";
import RoofSavingsCalculator from "@/components/site/RoofSavingsCalculator";

const IMG =
  "https://static.prod-images.emergentagent.com/jobs/50fbfbe1-e930-4cc7-8453-ea3806374a6a/images/0ebef75877cd30a8272b41228c7b311af269e76772c7023d2f72aa28f47f764e.jpeg";

const BENEFITS = [
  {
    n: "01",
    title: "Add 5–15 Years of Roof Life",
    desc: "A plant-based bio-oil treatment restores the flexibility and water-shedding power of aging asphalt shingles — each application adds up to five years, repeatable for fifteen.",
  },
  {
    n: "02",
    title: "A Fraction of Replacement Cost",
    desc: "Rejuvenation runs $2.50 per square foot — typically about half the cost of a full tear-off and replacement. Same roof, renewed performance, without the five-figure invoice.",
  },
  {
    n: "03",
    title: "Done in Hours, Not Weeks",
    desc: "No dumpsters, no nail-strewn lawn, no days of hammering. Most treatments are applied and cured in a single visit.",
  },
  {
    n: "04",
    title: "Expert Roof Repair",
    desc: "Missing shingles, flashing failures, leaks, and storm damage — repaired by the same craftsmen who build our custom homes. Small fixes handled before they become big problems.",
  },
];

const STATS = [
  { value: "5–15", label: "Years Added Per Roof" },
  { value: "~55%", label: "Savings vs. Replacement" },
  { value: "1 DAY", label: "Typical Treatment Time" },
];

export default function RoofRejuvenation() {
  return (
    <section
      id="roofing"
      data-testid="roof-rejuvenation-section"
      className="relative py-28 md:py-40 overflow-hidden bg-[var(--bg-secondary)] border-y border-white/10"
    >
      {/* Warm copper accent flourish */}
      <div
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none blur-3xl"
        style={{ background: "rgba(224, 192, 151, 0.06)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Heading row */}
        <div className="grid md:grid-cols-12 gap-10 mb-16 md:mb-20 items-end">
          <div className="md:col-span-7 reveal">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline w-10" />
              <span className="eyebrow" data-testid="roofing-eyebrow">
                Roof Rejuvenation &amp; Repair
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-6xl leading-[1.02] tracking-tight text-[var(--text-primary)]">
              Don't replace your roof.
              <br />
              <em className="text-[var(--accent)]">Rejuvenate it.</em>
            </h2>
          </div>
          <div className="md:col-span-5 reveal reveal-delay-1">
            <p className="text-[var(--text-secondary)] text-lg leading-relaxed">
              Most asphalt roofs are replaced years before they need to be. Our
              spray-applied rejuvenation treatment restores dried-out shingles,
              extends roof life by 5–15 years, and costs a fraction of a
              tear-off — with expert repair for anything beyond a treatment.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 md:gap-14">
          {/* Left: image + stats */}
          <div className="lg:col-span-6 reveal">
            <div className="relative overflow-hidden border border-white/10">
              <img
                src={IMG}
                alt="Roof rejuvenation treatment being applied to an asphalt shingle roof in New Jersey"
                className="w-full h-[420px] md:h-[520px] object-cover"
                loading="lazy"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to top, rgba(10,10,10,0.85) 0%, rgba(10,10,10,0.1) 45%, rgba(10,10,10,0.25) 100%)",
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div
                  className="grid grid-cols-3 gap-4"
                  data-testid="roofing-stats"
                >
                  {STATS.map((s) => (
                    <div key={s.label}>
                      <div className="font-display text-2xl md:text-4xl leading-none mb-2 text-[var(--accent)]">
                        {s.value}
                      </div>
                      <div className="font-body text-[9px] md:text-[10px] uppercase tracking-[0.22em] text-[var(--text-secondary)] leading-snug">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <p className="mt-6 font-body text-sm text-[var(--text-secondary)] leading-relaxed">
              <span className="text-[var(--text-primary)]">Eco-friendly by design</span>{" "}
              — every rejuvenated roof keeps roughly three tons of shingles out
              of a landfill and defers the manufacturing footprint of a brand-new
              roof.
            </p>
          </div>

          {/* Right: benefits */}
          <div className="lg:col-span-6 flex flex-col">
            <div className="grid gap-px bg-white/10 border border-white/10">
              {BENEFITS.map((b, i) => (
                <article
                  key={b.n}
                  className="reveal group p-7 md:p-8 bg-[var(--bg-primary)] transition-colors duration-500 hover:bg-black/60 flex gap-6"
                  style={{ transitionDelay: `${i * 70}ms` }}
                  data-testid={`roofing-benefit-${b.n}`}
                >
                  <span className="num-marker text-2xl md:text-3xl shrink-0">
                    {b.n}
                  </span>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl text-[var(--text-primary)] mb-2 leading-tight">
                      {b.title}
                    </h3>
                    <p className="text-[var(--text-secondary)] leading-relaxed text-[14px]">
                      {b.desc}
                    </p>
                  </div>
                </article>
              ))}
            </div>

            <div className="reveal mt-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
              <p className="font-body text-sm text-[var(--text-secondary)] max-w-sm leading-relaxed">
                Not sure if your roof qualifies? We'll inspect it and tell you
                honestly — rejuvenate, repair, or replace.
              </p>
              <a
                href="#contact"
                className="btn-solid w-fit shrink-0"
                data-testid="roofing-cta"
              >
                Get a Free Roof Assessment
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <BeforeAfterGallery />
        <RoofSavingsCalculator />
      </div>
    </section>
  );
}
